import React, { useState, useEffect } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import axios from 'axios';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  TablePagination,
} from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import { translator } from '../../utils/Translator';
import MainButton from "../UIElements/MainButton";

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root}>
      <Typography variant="h6">{children}</Typography>
      <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
        <CloseIcon />
      </IconButton>
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`price-tabpanel-${index}`}
      aria-labelledby={`price-tab-${index}`}
      {...other}
    >
      {value === index && <Box py={3}>{children}</Box>}
    </div>
  );
}

const DialogCheckPrice = ({ open, product, customer, onClose }) => {
  const { authUser } = useSelector(({ auth }) => auth);
  const { translate } = useSelector(({ i18n }) => i18n);

  const [loading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);

  // Pagination states for each tab
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Data states for each tab
  const [priceHistoryData, setPriceHistoryData] = useState([]);
  const [priceLevelData, setPriceLevelData] = useState([]);
  const [quotationData, setQuotationData] = useState([]);
  const [customerPriceData, setCustomerPriceData] = useState([]);
  const [groupStepPricingData, setGroupStepPricingData] = useState([]);
  const [groupPricingData, setGroupPricingData] = useState([]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setPage(0); // Reset to first page when changing tabs
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Fetch all data when dialog opens
  useEffect(() => {
    const fetchAllPriceData = async () => {
      if (!open || !product) return;

      setLoading(true);
      try {
        const config = {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('x-access-token')}`,
          },
        };

        // Call all APIs in parallel
        const [
          priceHistoryRes,
          priceLevelRes,
          quotationRes,
          customerPriceRes,
          groupStepPricingRes,
          groupPricingRes
        ] = await Promise.all([
          // ประวัติราคา
          axios.get(`${process.env.REACT_APP_API_URL}customerPriceHistory`, {
            ...config,
            // params: { company: "83", productCode: "SCB-002" } // สำหรับทดสอบ
            params: { company: authUser.company, productCode: product.ProductCode, customerCode: customer?.CustomerCode }
          }).catch(() => ({ data: { success: false, data: [] } })),

          // ระดับราคา
          axios.get(`${process.env.REACT_APP_API_URL}productPricingLevel`, {
            ...config,
            // params: { company: "33", productCode: "03613" } // สำหรับทดสอบ
            params: { company: authUser.company, productCode: product.ProductCode } // สำหรับทดสอบ
          }).catch(() => ({ data: { success: false, data: [] } })),

          // เสนอราคา
          axios.get(`${process.env.REACT_APP_API_URL}quotePriceHistory`, {
            ...config,
            // params: { company: "47", productCode: "CI-27-45007" } // สำหรับทดสอบ
            params: { company: authUser.company, productCode: product.ProductCode, customerCode: customer?.CustomerCode }
          }).catch(() => ({ data: { success: false, data: [] } })),

          // กำหนดราคาลูกค้า
          axios.get(`${process.env.REACT_APP_API_URL}customerStepPricing`, {
            ...config,
            // params: { company: "47", productCode: "00-37-60044" } // สำหรับทดสอบ
            params: { company: authUser.company, productCode: product.ProductCode }
          }).catch(() => ({ data: { success: false, data: [] } })),

          // กำหนดราคากลุ่ม
          axios.get(`${process.env.REACT_APP_API_URL}groupStepPricing`, {
            ...config,
            // params: { company: "47", productCode: "00-37-60044" } // สำหรับทดสอบ
            params: { company: authUser.company, productCode: product.ProductCode }
          }).catch(() => ({ data: { success: false, data: [] } })),

          // ราคาเฉพาะกลุ่มลูกค้า
          axios.get(`${process.env.REACT_APP_API_URL}groupPricing`, {
            ...config,
            // params: { company: "47", productCode: "00-37-60044" } // สำหรับทดสอบ
            params: { company: authUser.company, productCode: product.ProductCode }
          }).catch(() => ({ data: { success: false, data: [] } }))
        ]);

        // Set data for all tabs
        if (priceHistoryRes.data.success) {
          setPriceHistoryData(priceHistoryRes.data.data || []);
          console.log('📊 Price History Data:', priceHistoryRes.data);
        }
        if (priceLevelRes.data.success) {
          setPriceLevelData(priceLevelRes.data.data || []);
          console.log('📊 Price Level Data:', priceLevelRes.data);
        }
        if (quotationRes.data.success) {
          setQuotationData(quotationRes.data.data || []);
          console.log('📊 Quotation Data:', quotationRes.data);
        }
        if (customerPriceRes.data.success) {
          setCustomerPriceData(customerPriceRes.data.data || []);
          console.log('📊 Customer Price Data:', customerPriceRes.data);
        }
        if (groupStepPricingRes.data.success) {
          setGroupStepPricingData(groupStepPricingRes.data.data || []);
          console.log('📊 Group Step Pricing Data:', groupStepPricingRes.data);
        }
        if (groupPricingRes.data.success) {
          setGroupPricingData(groupPricingRes.data.data || []);
          console.log('📊 Group Pricing Data:', groupPricingRes.data);
        }
      } catch (error) {
        console.error('Error fetching price data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchAllPriceData();
    }
  }, [open, product, customer, authUser.company]);

  // Render table based on tab
  const renderPriceTable = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress style={{ color: "#ff6f00" }} />
        </Box>
      );
    }

    let data = [];
    let columns = [];

    switch (currentTab) {
      case 0: // ประวัติราคา
        data = priceHistoryData;
        columns = [
          { id: 'no', label: `${translator(translate.ISA_Sequence, "ลำดับ")}`, align: 'center', width: 80 },
          { id: 'date', label: `${translator(translate.ISA_Date, "วันที่")}`, align: 'center', width: 120 },
          { id: 'customer', label: `${translator(translate.ISA_Customer, "ลูกค้า")}`, align: 'left', width: 300 },
          { id: 'unit', label: `${translator(translate.ISA_Unit, "หน่วย")}`, align: 'center', width: 100 },
          { id: 'price', label: `${translator(translate.ISA_Price, "ราคา")}`, align: 'right', width: 120 },
          { id: 'discountDesc', label: `${translator(translate.ISA_DiscountDetail, "รายละเอียดส่วนลด")}`, align: 'left', width: 150 },
          { id: 'discount', label: `${translator(translate.ISA_DiscountPerUnit, "ส่วนลด/หน่วย")}`, align: 'right', width: 120 },
          { id: 'currency', label: `${translator(translate.ISA_Currency, "สกุลเงิน")}`, align: 'center', width: 100 },
        ];
        break;
      case 1: // ระดับราคา
        data = priceLevelData;
        columns = [
          { id: 'no', label: `${translator(translate.ISA_Sequence, "ลำดับ")}`, align: 'center', width: 80 },
          { id: 'level', label: `${translator(translate.ISA_PriceLevelTab, "ระดับราคา")}`, align: 'center', width: 100 },
          { id: 'description', label: `${translator(translate.ISA_ProductCustomName, "คำอธิบาย")}`, align: 'left', width: 200 },
          { id: 'price1', label: `${translator(translate.ISA_ProductPrice_1, "ราคา 1")}`, align: 'right', width: 120 },
          { id: 'price2', label: `${translator(translate.ISA_ProductPrice_2, "ราคา 2")}`, align: 'right', width: 120 },
          { id: 'price3', label: `${translator(translate.ISA_ProductPrice_3, "ราคา 3")}`, align: 'right', width: 120 },
          { id: 'price4', label: `${translator(translate.ISA_ProductPrice_4, "ราคา 4")}`, align: 'right', width: 120 },
          { id: 'currency', label: `${translator(translate.ISA_Currency, "สกุลเงิน")}`, align: 'center', width: 100 },
        ];
        break;
      case 2: // เสนอราคา
        data = quotationData;
        columns = [
          { id: 'no', label: `${translator(translate.ISA_Sequence, "ลำดับ")}`, align: 'center', width: 80 },
          { id: 'date', label: `${translator(translate.ISA_Date, "วันที่")}`, align: 'center', width: 120 },
          { id: 'quotationNo', label: `${translator(translate.ISA_QuotationNumber, "เลขที่ใบเสนอราคา")}`, align: 'left', width: 150 },
          { id: 'customer', label: `${translator(translate.ISA_Customer, "ลูกค้า")}`, align: 'left' },
          { id: 'unit', label: `${translator(translate.ISA_Unit, "หน่วย")}`, align: 'center', width: 100 },
          { id: 'quantity', label: `${translator(translate.ISA_Qty, "จำนวน")}`, align: 'right', width: 100 },
          { id: 'price', label: `${translator(translate.ISA_Price, "ราคา")}`, align: 'right', width: 120 },
          { id: 'discountDesc', label: `${translator(translate.ISA_DiscountDetail, "รายละเอียดส่วนลด")}`, align: 'left', width: 150 },
          { id: 'discount', label: `${translator(translate.ISA_DiscountPerUnit, "ส่วนลด/หน่วย")}`, align: 'right', width: 120 },
          { id: 'currency', label: `${translator(translate.ISA_Currency, "สกุลเงิน")}`, align: 'center', width: 100 },
        ];
        break;
      case 3: // กำหนดราคาลูกค้า
        data = customerPriceData;
        columns = [
          { id: 'no', label: `${translator(translate.ISA_Sequence, "ลำดับ")}`, align: 'center', width: 80 },
          { id: 'customer', label: `${translator(translate.ISA_Customer, "ลูกค้า")}`, align: 'left', width: 300 },
          { id: 'condition', label: `${translator(translate.ISA_Condition, "เงื่อนไข")}`, align: 'center', width: 100 },
          { id: 'price1', label: `${translator(translate.ISA_ProductPrice_1, "ราคา 1")}`, align: 'right', width: 120 },
          { id: 'price2', label: `${translator(translate.ISA_ProductPrice_2, "ราคา 2")}`, align: 'right', width: 120 },
          { id: 'price3', label: `${translator(translate.ISA_ProductPrice_3, "ราคา 3")}`, align: 'right', width: 120 },
          { id: 'price4', label: `${translator(translate.ISA_ProductPrice_4, "ราคา 4")}`, align: 'right', width: 120 },
          { id: 'currency', label: `${translator(translate.ISA_Currency, "สกุลเงิน")}`, align: 'center', width: 100 },
        ];
        break;
      case 4: // กำหนดราคากลุ่ม
        data = groupStepPricingData;
        columns = [
          { id: 'no', label: `${translator(translate.ISA_Sequence, "ลำดับ")}`, align: 'center', width: 80 },
          { id: 'group', label: `${translator(translate.ISA_CustomerGroup, "กลุ่มลูกค้า")}`, align: 'left', width: 300 },
          { id: 'condition', label: `${translator(translate.ISA_Condition, "เงื่อนไข")}`, align: 'center', width: 100 },
          { id: 'price1', label: `${translator(translate.ISA_ProductPrice_1, "ราคา 1")}`, align: 'right', width: 120 },
          { id: 'price2', label: `${translator(translate.ISA_ProductPrice_2, "ราคา 2")}`, align: 'right', width: 120 },
          { id: 'price3', label: `${translator(translate.ISA_ProductPrice_3, "ราคา 3")}`, align: 'right', width: 120 },
          { id: 'price4', label: `${translator(translate.ISA_ProductPrice_4, "ราคา 4")}`, align: 'right', width: 120 },
          { id: 'currency', label: `${translator(translate.ISA_Currency, "สกุลเงิน")}`, align: 'center', width: 100 },
        ];
        break;
      case 5: // ราคาแบบกลุ่มลูกค้า
        data = groupPricingData;
        columns = [
          { id: 'no', label: `${translator(translate.ISA_Sequence, "ลำดับ")}`, align: 'center', width: 80 },
          { id: 'group', label: `${translator(translate.ISA_CustomerGroup, "กลุ่มลูกค้า")}`, align: 'left', width: 300 },
          { id: 'price1', label: `${translator(translate.ISA_ProductPrice_1, "ราคา 1")}`, align: 'right', width: 120 },
          { id: 'price2', label: `${translator(translate.ISA_ProductPrice_2, "ราคา 2")}`, align: 'right', width: 120 },
          { id: 'price3', label: `${translator(translate.ISA_ProductPrice_3, "ราคา 3")}`, align: 'right', width: 120 },
          { id: 'price4', label: `${translator(translate.ISA_ProductPrice_4, "ราคา 4")}`, align: 'right', width: 120 },
          { id: 'currency', label: `${translator(translate.ISA_Currency, "สกุลเงิน")}`, align: 'center', width: 100 },
        ];
        break;
      default:
        break;
    }

    if (data.length === 0) {
      return (
        <Box p={4} textAlign="center">
          <Typography variant="body1" color="textSecondary">
            {translator(translate.ISA_NoDataFound, "ไม่พบข้อมูล")}
          </Typography>
        </Box>
      );
    }

    // Calculate pagination
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const paginatedData = data.slice(startIndex, endIndex);

    return (
      <>
        <TableContainer component={Paper} elevation={0} style={{ border: '1px solid #e0e0e0' }}>
          <Table>
            <TableHead style={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ fontWeight: 'bold', width: column.width }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((item, index) => {
                const actualIndex = startIndex + index;
                if (currentTab === 0) {
                  // ประวัติราคา
                  return (
                    <TableRow key={actualIndex} hover>
                      <TableCell align="center">{actualIndex + 1}</TableCell>
                      <TableCell align="center">
                        {item.TrxDate ? `${item.TrxDate.substring(6, 8)}/${item.TrxDate.substring(4, 6)}/${item.TrxDate.substring(0, 4)}` : '-'}
                      </TableCell>
                      <TableCell align="left">
                        {item.CustomerName && item.CustomerCode
                          ? `${item.CustomerName} (${item.CustomerCode})`
                          : item.CustomerName || item.CustomerCode || '-'}
                      </TableCell>
                      <TableCell align="center">{item.Unit || '-'}</TableCell>
                      <TableCell align="right">{item.UnitPrice ? item.UnitPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}</TableCell>
                      <TableCell align="left">{item.DiscountDescription || '-'}</TableCell>
                      <TableCell align="right">{item.DiscountPerUnit ? item.DiscountPerUnit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}</TableCell>
                      <TableCell align="center">{item.CurrencyCode || '-'}</TableCell>
                    </TableRow>
                  );
                }

                if (currentTab === 1) {
                  // ระดับราคา
                  return (
                    <TableRow key={actualIndex} hover>
                      <TableCell align="center">{actualIndex + 1}</TableCell>
                      <TableCell align="center">{item.LevelNo || '-'}</TableCell>
                      <TableCell align="left">{item.LevelDescription || '-'}</TableCell>
                      <TableCell align="right">{item.Price1 ? item.Price1.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}</TableCell>
                      <TableCell align="right">{item.Price2 ? item.Price2.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}</TableCell>
                      <TableCell align="right">{item.Price3 ? item.Price3.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}</TableCell>
                      <TableCell align="right">{item.Price4 ? item.Price4.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}</TableCell>
                      <TableCell align="center">{item.CurrencyCode || '-'}</TableCell>
                    </TableRow>
                  );
                }

                if (currentTab === 2) {
                  // เสนอราคา
                  return (
                    <TableRow key={actualIndex} hover>
                      <TableCell align="center">{actualIndex + 1}</TableCell>
                      <TableCell align="center">
                        {item.TrxDate ? `${item.TrxDate.substring(6, 8)}/${item.TrxDate.substring(4, 6)}/${item.TrxDate.substring(0, 4)}` : '-'}
                      </TableCell>
                      <TableCell align="left">{item.QuoteNumber || '-'}</TableCell>
                      <TableCell align="left">
                        {item.CustomerName && item.CustomerCode
                          ? `${item.CustomerName} (${item.CustomerCode})`
                          : item.CustomerName || item.CustomerCode || '-'}
                      </TableCell>
                      <TableCell align="center">{item.Unit || '-'}</TableCell>
                      <TableCell align="right">{item.Quantity ? item.Quantity.toLocaleString() : '0'}</TableCell>
                      <TableCell align="right">{item.UnitPrice ? item.UnitPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}</TableCell>
                      <TableCell align="left">{item.DiscountDescription || '-'}</TableCell>
                      <TableCell align="right">{item.DiscountPerUnit ? item.DiscountPerUnit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}</TableCell>
                      <TableCell align="center">{item.CurrencyCode || '-'}</TableCell>
                    </TableRow>
                  );
                }

                if (currentTab === 3) {
                  // กำหนดราคาลูกค้า
                  return (
                    <TableRow key={actualIndex} hover>
                      <TableCell align="center">{actualIndex + 1}</TableCell>
                      <TableCell align="left">
                        {item.CustomerName && item.CustomerCode
                          ? `${item.CustomerName} (${item.CustomerCode})`
                          : item.CustomerName || item.CustomerCode || '-'}
                      </TableCell>
                      <TableCell align="center">{item.Condition || '-'}</TableCell>
                      <TableCell align="right">{item.Price1 ? item.Price1.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}</TableCell>
                      <TableCell align="right">{item.Price2 ? item.Price2.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}</TableCell>
                      <TableCell align="right">{item.Price3 ? item.Price3.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}</TableCell>
                      <TableCell align="right">{item.Price4 ? item.Price4.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}</TableCell>
                      <TableCell align="center">{item.CurrencyCode || '-'}</TableCell>
                    </TableRow>
                  );
                }

                if (currentTab === 4) {
                  // กำหนดราคากลุ่ม
                  return (
                    <TableRow key={actualIndex} hover>
                      <TableCell align="center">{actualIndex + 1}</TableCell>
                      <TableCell align="left">
                        {item.GroupName && item.GroupCode
                          ? `${item.GroupName} (${item.GroupCode})`
                          : item.GroupName || item.GroupCode || '-'}
                      </TableCell>
                      <TableCell align="center">{item.Condition || '-'}</TableCell>
                      <TableCell align="right">{item.Price1 ? item.Price1.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}</TableCell>
                      <TableCell align="right">{item.Price2 ? item.Price2.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}</TableCell>
                      <TableCell align="right">{item.Price3 ? item.Price3.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}</TableCell>
                      <TableCell align="right">{item.Price4 ? item.Price4.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}</TableCell>
                      <TableCell align="center">{item.CurrencyCode || '-'}</TableCell>
                    </TableRow>
                  );
                }

                if (currentTab === 5) {
                  // ราคาแบบกลุ่มลูกค้า
                  return (
                    <TableRow key={actualIndex} hover>
                      <TableCell align="center">{actualIndex + 1}</TableCell>
                      <TableCell align="left">
                        {item.GroupName && item.GroupCode
                          ? `${item.GroupName} (${item.GroupCode})`
                          : item.GroupName || item.GroupCode || '-'}
                      </TableCell>
                      <TableCell align="right">{item.Price1 ? item.Price1.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}</TableCell>
                      <TableCell align="right">{item.Price2 ? item.Price2.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}</TableCell>
                      <TableCell align="right">{item.Price3 ? item.Price3.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}</TableCell>
                      <TableCell align="right">{item.Price4 ? item.Price4.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}</TableCell>
                      <TableCell align="center">{item.CurrencyCode || '-'}</TableCell>
                    </TableRow>
                  );
                }

                // Default rendering for other tabs
                return (
                  <TableRow key={actualIndex} hover>
                    <TableCell align="center">{actualIndex + 1}</TableCell>
                    <TableCell colSpan={columns.length - 1} align="center">
                      <Typography variant="caption" color="textSecondary">
                        {translator(translate.ISA_DataWillShowWhenAPIReady, "[ข้อมูลจะแสดงเมื่อ API พร้อม]")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={data.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          labelRowsPerPage={translator(translate.ISA_RowsPerPage, "แถวต่อหน้า:")}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${translator(translate.ISA_Of, "จาก")} ${count}`}
        />
      </>
    );
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={onClose}
    >
      <DialogTitle onClose={onClose}>
        <Box display="flex" alignItems="center">
          <AttachMoneyIcon style={{ marginRight: 8, color: "#ff6f00" }} />
          {translator(translate.ISA_CheckPrice, "ตรวจสอบราคา")}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Product Info */}
        {product && (
          <Box mb={3} p={2} bgcolor="#f5f5f5" borderRadius={2}>
            <Typography variant="h6" gutterBottom>
              {product.ProductName}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              รหัสสินค้า: {product.ProductCode}
            </Typography>
            {customer && (
              <Typography variant="body2" color="textSecondary">
                ลูกค้า: {customer.CustomerName} ({customer.CustomerCode})
              </Typography>
            )}
          </Box>
        )}

        {/* Tabs */}
        <Box mb={2}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            TabIndicatorProps={{
              style: { backgroundColor: '#ff6f00' }
            }}
          >
            <Tab
              label={translator(translate.ISA_PriceHistory, "ประวัติราคา")}
              style={currentTab === 0 ? { backgroundColor: '#fff3e0', fontWeight: 'bold' } : {}}
            />
            <Tab
              label={translator(translate.ISA_PriceLevelTab, "ระดับราคา")}
              style={currentTab === 1 ? { backgroundColor: '#fff3e0', fontWeight: 'bold' } : {}}
            />
            <Tab
              label={translator(translate.ISA_QuotationTab, "เสนอราคา")}
              style={currentTab === 2 ? { backgroundColor: '#fff3e0', fontWeight: 'bold' } : {}}
            />
            <Tab
              label={translator(translate.ISA_CustomerPriceSetting, "กำหนดราคาลูกค้า")}
              style={currentTab === 3 ? { backgroundColor: '#fff3e0', fontWeight: 'bold' } : {}}
            />
            <Tab
              label={translator(translate.ISA_GroupPriceSetting, "กำหนดราคากลุ่ม")}
              style={currentTab === 4 ? { backgroundColor: '#fff3e0', fontWeight: 'bold' } : {}}
            />
            <Tab
              label={translator(translate.ISA_GroupCustomerPrice, "ราคาเฉพาะกลุ่มลูกค้า")}
              style={currentTab === 5 ? { backgroundColor: '#fff3e0', fontWeight: 'bold' } : {}}
            />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <TabPanel value={currentTab} index={0}>
          {renderPriceTable()}
        </TabPanel>
        <TabPanel value={currentTab} index={1}>
          {renderPriceTable()}
        </TabPanel>
        <TabPanel value={currentTab} index={2}>
          {renderPriceTable()}
        </TabPanel>
        <TabPanel value={currentTab} index={3}>
          {renderPriceTable()}
        </TabPanel>
        <TabPanel value={currentTab} index={4}>
          {renderPriceTable()}
        </TabPanel>
        <TabPanel value={currentTab} index={5}>
          {renderPriceTable()}
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <MainButton
          label={translator(translate.ISA_Status_Closed, "ปิด")}
          handleClick={onClose}
        />
      </DialogActions>
    </Dialog>
  );
};

export default DialogCheckPrice;
